/* eslint-disable max-len */
import moment from 'moment'
import { getDate, formatDateZone } from '@utils/date-helpers'
import { mapGetters, mapState } from 'vuex'
import { KEY_STRING_MAP } from '@utils/keyboard-helpers'

export default {
  inject: ['updateLocalQuery', 'localQuery'],
  data() {
    return {
      dateRangeCache: {},
    }
  },
  watch: {
    monthData: {
      handler() {
        this.clearDateRangeCache()
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters('booking', ['showPricesWithTaxes']),
    ...mapState('booking', ['now']),
    ...mapGetters('booking', [
      'dayFormat',
      'dateRangeFormat',
    ]),
    ...mapGetters('currency', ['getDisplayPrice', 'orSelected']),
    ...mapGetters('hotelData', ['getHotelById']),

    selectedHotel() {
      return this.getHotelById(this.localQuery.hotelCode)
    },
    hotelCurrency() {
      return this.selectedHotel?.currency?.isoCode
    },
    ariaDateFormat() {
      return 'dddd[,] D MMMM YYYY'
    },
    inputNameMap() {
      return {
        stayDateStart: 'checkIn',
        stayDateEnd: 'checkOut',
      }
    },
    oneDayAfterStartDay() {
      return this.getDateFormatted(this.localQuery.stayDateStart).add(1, 'day')
    },
  },
  methods: {
    getDate,
    getMarginLeft(day) {
      return this.dayOfMonth(day) === 1 ? `${this.dayOfWeek(day) * 14.28}%` : 0
    },
    dayInDate(day) {
      return this.getDate(day)
    },
    dayOfMonth(day) {
      return this.dayInDate(day).date()
    },
    dayOfWeek(day) {
      return this.dayInDate(day).day()
    },
    getDateFormatted(day) {
      return this.getDate(day, this.dayFormat)
    },
    bindKeyboardEvents(e) {
      const el = e.target
      el.addEventListener('keydown', this.onKeydownEvent)
    },
    unbindKeyboardEvents(e) {
      const el = e.target
      el.removeEventListener('keydown', this.onKeydownEvent)
    },
    onKeydownEvent(e) {
      const { day } = e.target.dataset
      let elementId = null
      const date = getDate(day)
      if (e.key === KEY_STRING_MAP.ARROW_RIGHT) {
        date.add(1, 'day')
        elementId = date.format(this.dayFormat)
      } else if (e.key === KEY_STRING_MAP.ARROW_LEFT) {
        date.subtract(1, 'day')
        elementId = date.format(this.dayFormat)
      } else if (e.key === KEY_STRING_MAP.ARROW_UP) {
        date.subtract(7, 'day')
        elementId = date.format(this.dayFormat)
      } else if (e.key === KEY_STRING_MAP.ARROW_DOWN) {
        date.add(7, 'day')
        elementId = date.format(this.dayFormat)
      } else if (e.altKey && e.key === KEY_STRING_MAP.PAGE_DOWN) {
        date.add(1, 'year')
        elementId = date.format(this.dayFormat)
      } else if (e.altKey && e.key === KEY_STRING_MAP.PAGE_UP) {
        date.subtract(1, 'year')
        elementId = date.format(this.dayFormat)
      } else if (e.key === KEY_STRING_MAP.PAGE_DOWN) {
        date.subtract(1, 'month')
        elementId = date.format(this.dayFormat)
      } else if (e.key === KEY_STRING_MAP.PAGE_UP) {
        date.add(1, 'month')
        elementId = date.format(this.dayFormat)
      } else if (e.key === KEY_STRING_MAP.HOME) {
        date.startOf('week')
        elementId = date.format(this.dayFormat)
      } else if (e.key === KEY_STRING_MAP.END) {
        date.endOf('week')
        elementId = date.format(this.dayFormat)
      }

      if (elementId) {
        e.stopPropagation()
        e.preventDefault()
        document.getElementById(elementId)?.focus()
      }
    },
    showBackground() {
      return (
        this.localQuery.stayDateStart
        && this.localQuery.stayDateEnd
        && !this.getDateFormatted(this.localQuery.stayDateStart).isSame(
          this.localQuery.stayDateEnd
        )
      )
    },
    isStartDate(day) {
      return day?.split('T')[0] === this.localQuery?.stayDateStart?.split('T')[0]
    },
    isEndDate(day) {
      return day?.split('T')[0] === this.localQuery?.stayDateEnd?.split('T')[0]
    },
    isBetweenDate(day) {
      return (
        this.getDateFormatted(this.localQuery.stayDateStart).isBefore(day)
        && this.getDateFormatted(this.localQuery.stayDateEnd).isAfter(day)
      )
    },
    isPastDate(day) {
      return moment(day).locale('en').isBefore(new Date(), 'day')
    },
    dayIsInSameWeek(day) {
      return moment(day).locale('en').isSame(new Date(), 'week')
    },
    isBeforeStartDate(day) {
      if (
        this.inputSelected === 'stayDateStart'
        || !this.localQuery.stayDateStart
      ) {
        return false
      }
      return (
        this.getDateFormatted(this.localQuery.stayDateStart).isAfter(
          day,
          'day'
        ) || day.split('T')[0] === this.localQuery.stayDateStart
      )
    },
    firstClosedPeriod() {
      return (
        this.localQuery.stayDateStart
        && this.monthData.find(
          (dateRange) => dateRange.status === 'Close'
            && this.getDate(dateRange.startDay, this.dateRangeFormat).isAfter(
              this.getDateFormatted(this.localQuery.stayDateStart)
            )
        )
      )
    },
    isFirstDayClosed(day) {
      const firstClosedPeriod = this.firstClosedPeriod()
      const { dateRangeFormat } = this

      if (!firstClosedPeriod) return false

      const startDate = this.getDate(firstClosedPeriod.startDay, dateRangeFormat)
      const currentDate = this.dayInDate(day)

      return startDate.isSame(currentDate, 'day')
    },
    isStatusClosed(day) {
      if (
        this.inputSelected === 'stayDateStart'
        || !this.localQuery.stayDateStart
      ) {
        return this.dayStatus(day) === 'Close'
      }
      return !this.isFirstDayClosed(day) && this.dayStatus(day) === 'Close'
    },
    isDisabled(day) {
      const isBeforeToday = this.now.isAfter(this.dayInDate(day), 'day')
      const isClosed = this.isStatusClosed(day)
      const isBeforeStart = this.isBeforeStartDate(day)
      const isBetweenDate = this.isBetweenDate(day)
      const isFirstClosed = this.isFirstDayClosed(day)

      if (this.inputSelected === 'stayDateEnd') {
        return isBeforeToday
          || isBeforeStart
          || (!isBetweenDate && isClosed && !this.isEndDate(day) && !isFirstClosed)
      }

      return (
        isClosed
        || isBeforeToday
        || isBeforeStart
      )
    },
    errorStatus(day) {
      return this.isStatusClosed(day) && this.isBetweenDate(day)
    },
    isSelected(day) {
      return this.inputSelected === 'stayDateStart'
        ? this.isStartDate(day)
        : this.isEndDate(day)
    },
    dateRangeInfo(day) {
      const cacheKey = `${this.localQuery.hotelCode}-${this.month}-${day}`

      if (this.dateRangeCache[cacheKey]) {
        return this.dateRangeCache[cacheKey]
      }

      const result = this.monthData.find((range) => this.checkDateRangeInfo(this.dayInDate(day), range))

      this.dateRangeCache[cacheKey] = result

      return result || []
    },
    clearDateRangeCache() {
      this.dateRangeCache = {}
    },
    checkDateRangeInfo(day, dateRange) {
      const startDateMatch = getDate(
        dateRange.startDay,
        this.dateRangeFormat
      ).isSame(day, 'day')
      const endDateMatch = getDate(
        dateRange.endDay,
        this.dateRangeFormat
      ).isSame(day, 'day')
      const innerDateMatch = getDate(dateRange.startDay, this.dateRangeFormat).isBefore(
        day,
        'day'
      )
        && getDate(dateRange.endDay, this.dateRangeFormat).isAfter(day, 'day')
      return startDateMatch || endDateMatch || innerDateMatch
    },
    dayStatus(day) {
      return this.dateRangeInfo(day)?.status ?? 'Close'
    },
    dayPrice(day) {
      if (!this.dateRangeInfo(day)) {
        return 0
      }

      return this.showPricesWithTaxes(this.localQuery)
        ? this.dateRangeInfo(day).priceWithTaxes
        : this.dateRangeInfo(day).price
    },
    displayPrice(day) {
      return this.getDisplayPrice({
        price: this.dayPrice(day),
        fromCurrency: this.hotelCurrency,
        toCurrency: this.orSelected(this.hotelCurrency),
      })
    },
    showPrice(day) {
      const isSmallWidth = window.innerWidth < 490
      const isExtraSmallWidth = window.innerWidth < 375
      return (
        this.dayPrice(day)
        && (!this.dayStatus(day)
          || (this.dayStatus(day) !== 'Close'
            && this.now.isSameOrBefore(this.dayInDate(day), 'day')))
        && !(
          (isSmallWidth && this.displayPrice(day).length >= 9)
          || (isExtraSmallWidth && this.displayPrice(day).length >= 7)
        )
      )
    },
    ariaLabel(day) {
      if (this.isStartDate(day)) {
        return this.tc('accessibility.checkInDate', {
          day: formatDateZone(day, null, this.ariaDateFormat),
          ratePrice: `$${this.dayPrice(day)}`,
        })
      }

      if (this.isEndDate(day)) {
        return this.tc('accessibility.checkOutDate', {
          day: formatDateZone(day, null, this.ariaDateFormat),
          ratePrice: `$${this.dayPrice(day)}`,
        })
      }

      if (this.dayStatus(day) === 'Close' || this.isDisabled(day)) {
        return this.tc('accessibility.disabledDate', {
          day: formatDateZone(day, null, this.ariaDateFormat),
        })
      }

      if (this.isBetweenDate(day)) {
        return this.tc('accessibility.selectedDate', {
          day: formatDateZone(day, null, this.ariaDateFormat),
          ratePrice: `$${this.dayPrice(day)}`,
        })
      }

      return this.tc('accessibility.availableDate', {
        day: formatDateZone(day, null, this.ariaDateFormat),
        ratePrice: `$${this.dayPrice(day)}`,
      })
    },
    isDefaultSelected(day) {
      if (this.inputSelected === 'stayDateStart') {
        return (
          this.now.isSame(day, 'day') && !this.localQuery[this.inputSelected]
        )
      }
      if (!this.localQuery[this.inputSelected]) {
        return (
          this.now.isSame(day, 'day')
          || this.oneDayAfterStartDay.isSame(day, 'day')
        )
      }
      return false
    },

    getDayTabIndex(day) {
      const isStartOrEndDate = this.isStartDate(day) || this.isEndDate(day)

      return isStartOrEndDate
        || this.isDefaultSelected(day)
        || this.isFirstAvailableDay(day) ? '0' : '-1'
    },

    isFirstAvailableDay(day) {
      if (this.isDisabled(day)) {
        return false
      }

      const firstAvailableDay = this.daysInMonth.find((dayInfo) => !this.isDisabled(dayInfo.dayFormatted))

      // Check if the provided day is the first available day
      return firstAvailableDay && firstAvailableDay.dayFormatted === day
    },
  },
}
